/*
 * <cart-discount> — champ de code promo fonctionnel dans le panier et le tiroir.
 * Utilise le paramètre `discount` de /cart/update.js (support natif Shopify) :
 *   - appliquer : { discount: "CODE1,CODE2" }
 *   - retirer tout : { discount: "" }
 * Les codes réellement appliqués reviennent dans le JSON du panier (discount_codes),
 * chacun avec un booléen `applicable`. On re-rend la section panier pour que le
 * total affiché reflète bien la remise (ce n'est donc pas qu'un affichage).
 */
if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('[data-discount-form]');
        this.input = this.querySelector('[data-discount-input]');
        this.submit = this.querySelector('[data-discount-submit]');
        this.errorEl = this.querySelector('[data-discount-error]');
        this.appliedEl = this.querySelector('[data-discount-applied]');
        this.applied = [];

        if (this.form) {
          this.form.addEventListener('submit', (event) => {
            event.preventDefault();
            const code = (this.input.value || '').trim();
            if (!code) return;
            const codes = this.applicableCodes();
            if (!codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
              codes.push(code);
            }
            this.setBusy(true);
            this.update(codes, code);
          });
        }

        if (this.appliedEl) {
          this.appliedEl.addEventListener('click', (event) => {
            const button = event.target.closest('[data-discount-remove]');
            if (!button) return;
            event.preventDefault();
            const target = button.dataset.discountRemove.toLowerCase();
            const codes = this.applicableCodes().filter((code) => code.toLowerCase() !== target);
            this.setBusy(true);
            this.update(codes, null);
          });
        }

        this.loadApplied();
      }

      applicableCodes() {
        return this.applied.filter((discount) => discount.applicable).map((discount) => discount.code);
      }

      // Les codes promo manuels ne sont pas exposés par Liquid : on lit /cart.js.
      loadApplied() {
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            this.applied = cart.discount_codes || [];
            this.renderApplied();
          })
          .catch(() => {});
      }

      update(codes, addedCode) {
        const sections = this.sectionsToRequest();
        const body = JSON.stringify({
          discount: codes.join(','),
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((cart) => {
            if (!cart || cart.status || cart.errors) {
              this.showError('Une erreur est survenue. Réessaie.');
              return;
            }

            this.applied = cart.discount_codes || [];

            if (addedCode) {
              const match = this.applied.find((discount) => discount.code.toLowerCase() === addedCode.toLowerCase());
              if (!match || !match.applicable) {
                this.showError(`Le code « ${addedCode} » n'est pas valide.`);
                // On purge le code invalide pour ne pas le laisser traîner côté serveur.
                if (match && !match.applicable) {
                  this.renderApplied();
                  return this.update(this.applicableCodes(), null);
                }
                this.renderSections(cart);
                this.renderApplied();
                return;
              }
              this.clearError();
              this.input.value = '';
            } else {
              this.clearError();
            }

            this.renderSections(cart);
            this.renderApplied();

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: cart });
            }
          })
          .catch(() => this.showError('Une erreur est survenue. Réessaie.'))
          .finally(() => this.setBusy(false));
      }

      sectionsToRequest() {
        if (this.closest('cart-drawer')) {
          return [
            {
              id: 'cart-drawer',
              section: 'cart-drawer',
              drawer: true,
              selectors: ['cart-drawer-items', '.cart-drawer__footer'],
            },
          ];
        }
        const list = [];
        const footer = document.getElementById('main-cart-footer');
        const items = document.getElementById('main-cart-items');
        if (footer) list.push({ id: 'main-cart-footer', section: footer.dataset.id, selectors: ['.js-contents'] });
        if (items) list.push({ id: 'main-cart-items', section: items.dataset.id, selectors: ['.js-contents'] });
        return list;
      }

      renderSections(cart) {
        if (!cart.sections) return;
        const parser = new DOMParser();
        this.sectionsToRequest().forEach((section) => {
          const html = cart.sections[section.section];
          if (!html) return;
          const doc = parser.parseFromString(html, 'text/html');
          section.selectors.forEach((selector) => {
            const source = doc.querySelector(selector);
            if (!source) return;
            if (section.drawer) {
              const target = document.querySelector(selector);
              if (target) target.replaceWith(source);
            } else {
              const container = document.getElementById(section.id);
              const target = container ? container.querySelector(selector) : null;
              if (target) target.innerHTML = source.innerHTML;
            }
          });
        });
      }

      renderApplied() {
        if (!this.appliedEl) return;
        const applicable = this.applied.filter((discount) => discount.applicable);
        this.appliedEl.innerHTML = applicable
          .map((discount) => {
            const code = discount.code;
            const safe = code.replace(/"/g, '&quot;');
            return `
              <li class="cart-discount__tag">
                <span class="cart-discount__tag-code">${this.escape(code)}</span>
                <button
                  type="button"
                  class="cart-discount__remove"
                  data-discount-remove="${safe}"
                  aria-label="Retirer le code ${this.escape(code)}"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </li>`;
          })
          .join('');
        this.appliedEl.hidden = applicable.length === 0;
      }

      escape(value) {
        const div = document.createElement('div');
        div.textContent = value;
        return div.innerHTML;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      setBusy(state) {
        this.toggleAttribute('aria-busy', state);
        if (this.submit) this.submit.disabled = state;
      }
    }
  );
}
